/*
 * Everything that touches Office.js / the Word JavaScript API lives here, so
 * the pane itself stays a plain React component that can be tested with this
 * module mocked.
 */
import { TAG_KIND, findTags } from "./wordAddinModel";

const OFFICE_JS = "https://appsforoffice.microsoft.com/lib/1/hosted/office.js";

let readyPromise = null;

// Loads Office.js once and resolves with { host, platform } (host is null
// when the page is opened outside Office, e.g. in a normal browser tab).
export const loadOffice = () => {
  if (readyPromise) return readyPromise;
  readyPromise = new Promise((resolve, reject) => {
    // Office.js replaces history.pushState/replaceState with no-ops inside
    // some hosts, which breaks React Router. Keep the originals.
    const { pushState, replaceState } = window.history;
    const restoreHistory = () => {
      if (typeof window.history.pushState !== "function") window.history.pushState = pushState;
      if (typeof window.history.replaceState !== "function") window.history.replaceState = replaceState;
    };
    const finish = () => {
      restoreHistory();
      if (!window.Office?.onReady) {
        resolve({ host: null, platform: null });
        return;
      }
      window.Office.onReady((info) => {
        restoreHistory();
        resolve({ host: info?.host || null, platform: info?.platform || null });
      });
    };
    if (window.Office?.onReady) {
      finish();
      return;
    }
    const script = document.createElement("script");
    script.src = OFFICE_JS;
    script.async = true;
    script.onload = finish;
    script.onerror = () => {
      restoreHistory();
      reject(new Error("Office.js could not be loaded."));
    };
    document.head.appendChild(script);
  });
  return readyPromise;
};

export const isWord = (info) => Boolean(info?.host) && String(info.host).toLowerCase() === "word";

export const documentUrl = () => window.Office?.context?.document?.url || "";

const run = (fn) => window.Word.run(fn);

// Insert a tag at the cursor (replacing any selection). Signature tags are
// written in white so they never print; "reveal" highlights make them visible.
export const insertAtCursor = async (text, { color = null, highlight = null } = {}) =>
  run(async (context) => {
    const selection = context.document.getSelection();
    selection.load("font/color");
    await context.sync();
    const previousColor = selection.font.color && selection.font.color !== "" ? selection.font.color : "#000000";

    const inserted = selection.insertText(text, "Replace");
    if (color) inserted.font.color = color;
    if (highlight) inserted.font.highlightColor = highlight;

    // A following space in the original colour, so typing continues normally
    // instead of inheriting white text or a highlight.
    const trailing = inserted.insertText(" ", "After");
    trailing.font.color = previousColor;
    trailing.font.highlightColor = null;
    trailing.getRange("End").select();
    await context.sync();
  });

// Body plus every section's primary header and footer — signature tags are
// often parked in a footer.
const textAreas = (context) => {
  const body = context.document.body;
  const sections = context.document.sections;
  sections.load("items");
  return { body, sections };
};

const collectAreas = async (context) => {
  const { body, sections } = textAreas(context);
  await context.sync();
  const areas = [body];
  sections.items.forEach((section) => {
    areas.push(section.getHeader("Primary"));
    areas.push(section.getFooter("Primary"));
  });
  return areas;
};

// Every tag in the document, with kind. Reads paragraph text and matches in
// JS, so odd characters never have to survive Word's wildcard syntax.
export const scanTags = async () =>
  run(async (context) => {
    const areas = await collectAreas(context);
    const paragraphSets = areas.map((area) => {
      const paragraphs = area.paragraphs;
      paragraphs.load("items/text");
      return paragraphs;
    });
    await context.sync();
    const tags = [];
    paragraphSets.forEach((set) => set.items.forEach((p) => tags.push(...findTags(p.text))));
    return tags;
  });

// Highlight (or un-highlight) every tag, by literal search for each distinct
// tag string. `colours` maps kind -> Office highlight name.
export const setReveal = async (on, colours) =>
  run(async (context) => {
    const areas = await collectAreas(context);
    const paragraphSets = areas.map((area) => {
      const paragraphs = area.paragraphs;
      paragraphs.load("items/text");
      return paragraphs;
    });
    await context.sync();

    const distinct = new Map();
    paragraphSets.forEach((set) =>
      set.items.forEach((p) => findTags(p.text).forEach((t) => distinct.set(t.text, t.kind)))
    );
    if (!distinct.size) return 0;

    const results = [];
    areas.forEach((area) => {
      distinct.forEach((kind, text) => {
        // Word's search string is capped at 255 characters.
        if (text.length > 255) return;
        const found = area.search(text, { matchCase: true, matchWildcards: false });
        found.load("items");
        results.push({ found, kind });
      });
    });
    await context.sync();

    let touched = 0;
    results.forEach(({ found, kind }) => {
      found.items.forEach((range) => {
        // colours is keyed the way the server sends it: fields / signatures / conditionals.
        const colour = colours[TAG_KIND[kind]?.highlightKey] || null;
        range.font.highlightColor = on ? colour : null;
        touched += 1;
      });
    });
    await context.sync();
    return touched;
  });

// ---- Review my brief ------------------------------------------------------

// Tracked changes and comments arrived in the Word API's 1.4 requirement
// set; Word on the web and current desktop builds have it.
export const supportsReview = () =>
  Boolean(window.Office?.context?.requirements?.isSetSupported?.("WordApi", "1.4"));

// Every body paragraph, in Word's order, with its position. Positions are
// what the review refers back to, so empty paragraphs are kept.
export const readParagraphs = async () =>
  run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items/text,items/style,items/tableNestingLevel");
    await context.sync();
    return paragraphs.items.map((p, index) => ({
      index,
      text: p.text || "",
      style: p.style || "",
      in_table: Number(p.tableNestingLevel || 0) > 0,
    }));
  });

// Word's search treats ^ as an escape (^p, ^t …); everything else is literal
// with wildcards off.
const searchable = (text) => String(text).replace(/\^/g, "^^");

const loadParagraphs = async (context) => {
  const paragraphs = context.document.body.paragraphs;
  paragraphs.load("items");
  await context.sync();
  return paragraphs.items;
};

// `items` lets a caller that works through many suggestions read the
// paragraph list once instead of once per suggestion.
const paragraphAt = async (context, index, items) => (items || (await loadParagraphs(context)))[index] || null;

// Word will not search for more than this many characters.
const MAX_SEARCH_CHARS = 255;
const GONE = "That text isn't in the document any more — it may already be changed.";
const AMBIGUOUS = "That text now appears in more than one place; make this change by hand.";

// The range holding `original`: inside its paragraph first — the review
// says which copy it means when the phrase appears more than once — and
// failing that, the one place in the body it still appears (the author
// may have moved it).
const locate = async (context, { paragraph, original, occurrence }, items) => {
  if (String(original).length > MAX_SEARCH_CHARS) {
    throw new Error("That passage is too long for Word to find; make this change by hand.");
  }
  const target = await paragraphAt(context, paragraph, items);
  const options = { matchCase: true, matchWildcards: false };
  const inParagraph = target ? target.search(searchable(original), options) : null;
  if (inParagraph) inParagraph.load("items");
  const inBody = context.document.body.search(searchable(original), options);
  inBody.load("items");
  await context.sync();
  const hits = inParagraph ? inParagraph.items : [];
  if (hits.length) {
    const wanted = Number(occurrence) || 0;
    if (wanted && hits.length >= wanted) return hits[wanted - 1];
    // Only one copy left: the others have been changed already.
    if (hits.length === 1) return hits[0];
    throw new Error(AMBIGUOUS);
  }
  if (inBody.items.length === 1) return inBody.items[0];
  if (!inBody.items.length) throw new Error(GONE);
  throw new Error(AMBIGUOUS);
};

// Writes to the document go one at a time: applyEdit flips the document's
// Track Changes setting around its insert, so two running together could
// leave one edit untracked.
let writes = Promise.resolve();
const serially = (work) => {
  const turn = writes.then(work, work);
  writes = turn.catch(() => {});
  return turn;
};

// Replace `original` with `replacement` as a tracked change, whatever the
// author's own Track Changes setting is, and leave that setting as it was.
export const applyEdit = ({ paragraph, original, replacement, occurrence }) =>
  serially(() => run(async (context) => {
    const range = await locate(context, { paragraph, original, occurrence });
    const doc = context.document;
    doc.load("changeTrackingMode");
    await context.sync();
    const previous = doc.changeTrackingMode;
    const wasOff = !previous || previous === "Off";
    if (wasOff) doc.changeTrackingMode = "TrackAll";
    try {
      const inserted = range.insertText(replacement, "Replace");
      inserted.select();
      await context.sync();
    } finally {
      if (wasOff) {
        doc.changeTrackingMode = "Off";
        await context.sync();
      }
    }
    return true;
  }));

// The range a note is about: the words it quotes when they are still in
// the paragraph, otherwise the whole paragraph.
const noteRange = async (context, { paragraph, quote }, items) => {
  const target = await paragraphAt(context, paragraph, items);
  if (!target) throw new Error("That paragraph isn't in the document any more.");
  if (quote && String(quote).length <= MAX_SEARCH_CHARS) {
    const hits = target.search(searchable(quote), { matchCase: true, matchWildcards: false });
    hits.load("items");
    await context.sync();
    if (hits.items.length) return hits.items[0];
  }
  return target.getRange("Content");
};

// A margin comment on the words a note is about (or its paragraph).
export const addComment = ({ paragraph, quote, text }) =>
  serially(() => run(async (context) => {
    const range = await noteRange(context, { paragraph, quote });
    range.insertComment(text);
    await context.sync();
    return true;
  }));

// Select the whole paragraph, so the reader can see it (a caret at its
// start is invisible on a full page).
export const goToParagraph = async (paragraph) =>
  run(async (context) => {
    const target = await paragraphAt(context, paragraph);
    if (!target) throw new Error("That paragraph isn't in the document any more.");
    target.select();
    await context.sync();
    return true;
  });

// Select the exact words a line edit is about; the paragraph if they have
// already been changed.
export const goToEdit = async ({ paragraph, original, occurrence }) =>
  run(async (context) => {
    let range;
    try {
      range = await locate(context, { paragraph, original, occurrence });
    } catch (err) {
      range = await paragraphAt(context, paragraph);
      if (!range) throw err;
    }
    range.select();
    await context.sync();
    return true;
  });

// ---- Marks: every suggestion as a numbered comment in the document --------

export const MARK_PREFIX = "[WHG Docs review";

const ourComments = async (context) => {
  const comments = context.document.body.getComments();
  comments.load("items/content");
  await context.sync();
  return comments.items.filter((c) => String(c.content || "").startsWith(MARK_PREFIX));
};

// Put every mark in the document as a comment on its words: an edit on the
// phrase it changes, a note on the words it quotes or its paragraph, and a
// pointer on each other paragraph a note involves. Earlier marks of ours are
// removed first, so marking twice does not double up. Returns how many
// landed, the ids of the suggestions now pinned, and the labels of anything
// whose text could not be found.
export const markDocument = (marks) =>
  serially(() => run(async (context) => {
    for (const stale of await ourComments(context)) stale.delete();
    await context.sync();
    const items = await loadParagraphs(context);
    const missed = [];
    const placedIds = [];
    let placed = 0;
    for (const mark of marks) {
      try {
        const range =
          mark.target === "edit"
            ? await locate(context, mark, items)
            : await noteRange(context, mark, items);
        range.insertComment(mark.text);
        await context.sync();
        placed += 1;
        if (mark.id) placedIds.push(mark.id);
      } catch (err) {
        missed.push(mark.label);
      }
    }
    return { placed, missed, placedIds };
  }));

// Remove every comment of ours (and nobody else's). Returns how many.
export const clearMarks = () =>
  serially(() => run(async (context) => {
    const ours = await ourComments(context);
    for (const comment of ours) comment.delete();
    await context.sync();
    return ours.length;
  }));

// Remove the comments for one suggestion once it has been dealt with.
export const clearMark = (label) =>
  serially(() => run(async (context) => {
    const ours = (await ourComments(context)).filter((c) => String(c.content).startsWith(`${MARK_PREFIX} — ${label}`));
    for (const comment of ours) comment.delete();
    await context.sync();
    return ours.length;
  }));

// Open a same-origin page in an Office dialog and resolve with the first
// message it sends back (used for signing in).
export const openDialog = (url, { height = 60, width = 35 } = {}) =>
  new Promise((resolve, reject) => {
    const ui = window.Office?.context?.ui;
    if (!ui?.displayDialogAsync) {
      reject(new Error("Dialogs are not available here."));
      return;
    }
    ui.displayDialogAsync(url, { height, width, promptBeforeOpen: false }, (result) => {
      if (result.status !== window.Office.AsyncResultStatus.Succeeded) {
        reject(new Error(result.error?.message || "Could not open the sign-in window."));
        return;
      }
      const dialog = result.value;
      dialog.addEventHandler(window.Office.EventType.DialogMessageReceived, (arg) => {
        try {
          dialog.close();
        } catch (e) {
          // already closed
        }
        resolve(arg.message);
      });
      dialog.addEventHandler(window.Office.EventType.DialogEventReceived, () => {
        reject(new Error("The sign-in window was closed."));
      });
    });
  });

export const messageParent = (message) => {
  window.Office?.context?.ui?.messageParent?.(message);
};
